import { Banner } from '../primitives';

/**
 * The two agent-archive outcomes this banner can announce (Task 2.8).
 *
 * - `archive-document`: the agent archived the open document. The
 *   click-through deep-links to the Documents list with the archived
 *   lifecycle filter pre-applied.
 * - `archive-work-item`: the agent archived the open work item. The
 *   click-through routes to the Work Items list (no dedicated archive view
 *   exists; `/work-items` is the resolved destination).
 */
export type AgentArchiveBannerMode = 'archive-document' | 'archive-work-item';

export interface AgentArchiveBannerProps {
  /** Which archive outcome this banner announces. */
  mode: AgentArchiveBannerMode;
}

/**
 * Documents archived deep-link. Under the app's `HashRouter` the client route
 * + its query both live in the URL fragment, so the href is a hash route with
 * an embedded query string. `Documents.tsx` reads `lifecycle` via
 * `useSearchParams()` (router-aware) on load and initializes its list VM's
 * lifecycle filter to `archived`, so navigating here lands on `/documents`
 * with the archived filter active.
 */
const DOCUMENTS_ARCHIVE_HREF = '#/documents?lifecycle=archived';
/** Work Items list — the resolved destination for the work-item banner. */
const WORK_ITEMS_HREF = '#/work-items';

const COPY: Readonly<Record<AgentArchiveBannerMode, string>> = {
  'archive-document': 'Agent archived this document.',
  'archive-work-item': 'Agent archived this work item.'
};

const HREF: Readonly<Record<AgentArchiveBannerMode, string>> = {
  'archive-document': DOCUMENTS_ARCHIVE_HREF,
  'archive-work-item': WORK_ITEMS_HREF
};

/**
 * Thin DOMAIN wrapper composing the global `<Banner kind="warn">` primitive
 * (Task 2.8).
 *
 * Surfaces the VERBATIM agent-archive copy for two modes plus a click-through
 * anchor to the relevant archive list. NON-DISMISSIBLE: the `<Banner>`
 * primitive ships no dismiss affordance, so the banner persists with no extra
 * code — this wrapper deliberately adds none.
 *
 * No new CSS — visuals come entirely from the `pn-banner` / `pn-banner-warn`
 * primitive. The `data-testid="agent-archive-banner"` element is the Banner
 * root itself (the `kind="warn"` `<div>`); the anchor carries
 * `data-testid="agent-archive-banner-link"`.
 */
export function AgentArchiveBanner({ mode }: AgentArchiveBannerProps) {
  return (
    <Banner kind="warn" data-testid="agent-archive-banner">
      {COPY[mode]}{' '}
      <a href={HREF[mode]} data-testid="agent-archive-banner-link">
        Open archive list
      </a>
    </Banner>
  );
}
